import { Alert, Button } from "@softmax/design-system";
import type { RefObject } from "react";
import { CredentialDialog } from "@softmax/product-ux/account";
import { credentialPath } from "./credentials.queries";
import type { CredentialIntent } from "./credential-intent";
import {
  credentialDialogState,
  credentialFeedback,
} from "./credential-dialog-state";
import { useCredentialOperation } from "./use-credential-operation";

type CredentialOperationProps = {
  intent: CredentialIntent;
  finalFocusRef: RefObject<HTMLElement | null>;
  onClose(): void;
};

export function CredentialOperation(props: CredentialOperationProps) {
  const { intent } = props;
  const target = credentialPath({ target: intent.target });
  const operation = intent.action === "revoke" ? intent.record.id : "create";
  return <CredentialForm key={`${target}:${operation}`} {...props} />;
}

function CredentialForm({
  intent,
  finalFocusRef,
  onClose,
}: CredentialOperationProps) {
  const operation = useCredentialOperation({ intent, finalFocusRef, onClose });
  const {
    session,
    authorized,
    name,
    allowWrite,
    token,
    copy,
    mutation,
    uncertain,
    query,
    unavailable,
    close,
  } = operation;
  const { error, ...feedback } = credentialFeedback({
    uncertain,
    error: mutation.error,
    refreshError: query.error,
    refreshedAt: query.dataUpdatedAt,
    submittedAt: mutation.submittedAt,
    refreshing: query.isFetching,
  });

  const state = credentialDialogState({
    token,
    copied: copy.copied,
    copyError: copy.error,
    action: intent.action,
    name: intent.action === "revoke" ? intent.record.name : name,
    allowWrite: intent.target.kind === "user" ? allowWrite : undefined,
    pending: mutation.isPending,
    error,
  });
  const targetName =
    intent.target.kind === "user" ? "account" : intent.target.playerName;

  if (unavailable) return null;

  return (
    <CredentialDialog
      open
      disabled={!authorized}
      notice={
        !authorized && (
          <Alert>
            Verify your account to continue.{" "}
            <Button onClick={() => void session.refresh()}>
              Retry account
            </Button>
          </Alert>
        )
      }
      title={`${intent.action === "create" ? "Create" : "Revoke"} credential · ${targetName}`}
      state={state}
      finalFocusRef={finalFocusRef}
      onClose={close}
      onNameChange={operation.changeName}
      onWriteChange={operation.setAllowWrite}
      onCopy={operation.copyToken}
      {...feedback}
      onRefresh={operation.refresh}
      onSubmit={operation.submit}
    />
  );
}
